import {
  Component,
  ElementRef,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { NavigationStart, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';

import { NotificationService } from '../../../../core/services/notification.service';
import { WebPushService } from '../../../../core/services/web-push.service';
import { LayoutService } from '../../../../core/services/layout.service';
import { ButtonComponent } from '../../button/button.component';
import { LucideIconComponent } from '../../lucide-icon/lucide-icon.component';
import { NotificationPanelComponent } from '../notification-panel/notification-panel.component';

/**
 * Notification bell: unread badge + red "Needs action" pill (🔔 12 · ⚠ 3).
 * Opens the notifications panel as an anchored popover (desktop/tablet) or bottom sheet (mobile).
 *
 * - `placement="sidebar"`: in the desktop sidebar header; panel opens to the right of the bell.
 * - `placement="header"`:  in the mobile/tablet main header; panel opens below, right-aligned.
 */
@Component({
  selector: 'app-notification-bell',
  standalone: true,
  imports: [ButtonComponent, LucideIconComponent, NotificationPanelComponent],
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.css',
})
export class NotificationBellComponent implements OnDestroy {
  protected readonly notifications = inject(NotificationService);
  private readonly webPush = inject(WebPushService);
  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly layout = inject(LayoutService);
  private readonly router = inject(Router);

  readonly placement = input<'sidebar' | 'header'>('header');
  /** Sidebar collapsed → compact bell with overlaid count dots. */
  readonly collapsed = input(false);

  private readonly anchor = viewChild.required<ElementRef<HTMLElement>>('anchor');
  private readonly panelTpl = viewChild.required<TemplateRef<unknown>>('panelTpl');

  protected readonly isOpen = signal(false);
  protected readonly isMobile = computed(() => this.layout.isMobile());

  protected readonly unread = computed(() => this.notifications.unreadCount());
  protected readonly needs = computed(() => this.notifications.needsActionCount());
  protected readonly hasCritical = computed(() => this.notifications.needsActionBySeverity().critical > 0);
  protected readonly bellRinging = signal(false);

  protected readonly ariaLabel = computed(() => {
    const parts = [`Notifications`];
    if (this.unread()) parts.push(`${this.unread()} unread`);
    if (this.needs()) parts.push(`${this.needs()} need action`);
    return parts.join(', ');
  });

  private overlayRef: OverlayRef | null = null;
  private subs = new Subscription();
  private prevTotalCount: number | null = null;
  private ringTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    void this.webPush.init();
    // Close on navigation.
    this.subs.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => this.close())
    );

    effect(() => {
      const loading = this.notifications.inboxLoading() || this.notifications.needsActionLoading();
      const total = this.unread() + this.needs();
      if (loading) return;
      if (this.prevTotalCount !== null && total > this.prevTotalCount) {
        this.bellRinging.set(true);
        if (this.ringTimer) clearTimeout(this.ringTimer);
        this.ringTimer = setTimeout(() => {
          this.bellRinging.set(false);
          this.ringTimer = null;
        }, 550);
      }
      this.prevTotalCount = total;
    });
  }

  protected fmt(n: number): string {
    return n > 99 ? '99+' : String(n);
  }

  toggle(): void {
    if (this.isOpen()) this.close();
    else this.open();
  }

  open(): void {
    if (this.overlayRef) return;
    const mobile = this.isMobile();
    const positionStrategy = mobile
      ? this.overlay.position().global().bottom('0').left('0')
      : this.overlay
          .position()
          .flexibleConnectedTo(this.anchor())
          .withPush(true)
          .withViewportMargin(8)
          .withPositions(
            this.placement() === 'sidebar'
              ? [
                  { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 12 },
                  { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 12 },
                ]
              : [
                  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
                  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
                ]
          );

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: mobile ? this.overlay.scrollStrategies.block() : this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: mobile ? 'cdk-overlay-dark-backdrop' : 'cdk-overlay-transparent-backdrop',
      panelClass: 'notification-panel-pane',
    });
    this.overlayRef.attach(new TemplatePortal(this.panelTpl(), this.vcr));
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.keydownEvents().subscribe((e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.close(true);
      }
    });
    this.isOpen.set(true);
  }

  close(restoreFocus = false): void {
    if (!this.overlayRef) return;
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.isOpen.set(false);
    if (restoreFocus) {
      this.anchor().nativeElement.querySelector<HTMLElement>('button, a')?.focus();
    }
  }

  ngOnDestroy(): void {
    if (this.ringTimer) clearTimeout(this.ringTimer);
    this.subs.unsubscribe();
    this.overlayRef?.dispose();
  }
}
